






























import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientPartner } from "@/data/models/ClientPartner";
import { setClientPartnerCredentials } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { CREDENTIAL_FIELDS, SECRET_CREDENTIAL_FIELDS, partnerErrorKey } from "@/utils/ClientPartners";
import { PartnerDialogResult } from "@/components/admin/clients/partners/ClientPartnerFieldsDialog.vue";

const PARTNERS = "admin.clients.partners";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerCredentialsDialog extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  partner: ClientPartner | null = null;
  form: Record<string, string> = {};
  shown: Record<string, string> = {};

  private resolve: ((result: PartnerDialogResult) => void) | null = null;

  get fields(): string[] {
    const signIn = this.partner?.connection.signIn;
    return signIn ? CREDENTIAL_FIELDS[signIn] : [];
  }

  /** The fields to send: a secret typed in, an identifier changed. */
  get credentials(): Record<string, string> {
    const form = { ...this.form };
    const credentials: Record<string, string> = {};
    for (const field of this.fields) {
      const value = (form[field] ?? "").trim();
      if (value !== "" && value !== (this.shown[field] ?? "")) {
        credentials[field] = value;
      }
    }
    return credentials;
  }

  get hasChanged(): boolean {
    return Object.keys(this.credentials).length > 0;
  }

  isSecret(field: string): boolean {
    return SECRET_CREDENTIAL_FIELDS.includes(field);
  }

  secretLabelKey(field: string): string {
    const set = this.partner?.connection.secretsSet[field as "apiKey" | "password"] === true;
    return `${PARTNERS}.credentials.${set ? "secretSetLabel" : "secretMissingLabel"}`;
  }

  show(clientId: string, partner: ClientPartner): Promise<PartnerDialogResult> {
    this.clientId = clientId;
    this.partner = partner;
    const shown: Record<string, string> = {};
    for (const field of CREDENTIAL_FIELDS[partner.connection.signIn ?? "API_KEY"]) {
      shown[field] = this.isSecret(field) ? "" : partner.connection.identifiers[field as keyof ClientPartner["connection"]["identifiers"]] ?? "";
    }
    this.shown = shown;
    this.form = { ...shown };
    this.baseDialog.show({
      title: this.$t(`${PARTNERS}.editTitles.PARTNER_CREDENTIALS`, [partner.name]).toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged || !this.partner) {
      return;
    }
    this.busy = true;
    try {
      const saved = await setClientPartnerCredentials(this.clientId, this.partner.id, this.credentials);
      SnackbarModule.showSuccess({ key: `${PARTNERS}.credentials.saved` });
      this.close(saved);
    } catch (error) {
      SnackbarModule.showError({ key: partnerErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  close(result: PartnerDialogResult) {
    // The typed secrets leave memory with the dialog.
    this.form = {};
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: PartnerDialogResult) {
    this.form = {};
    this.resolve?.(result);
    this.resolve = null;
  }
}
